/**
 * Inert product-analytics provider for ReturnCatalyst.
 *
 * DESIGN CONTRACT (do not weaken):
 *   1. NO-OP BY DEFAULT. Nothing is sent unless product analytics is enabled,
 *      `VITE_POSTHOG_KEY` is present, and explicit user consent is true.
 *   2. PRIVACY FIRST. Browser-level opt-outs (DNT / GPC) override consent, and
 *      autocapture/pageview capture are disabled so only explicit events can send.
 *   3. NO PII. Only opaque IDs and the typed event shapes from events.ts are
 *      ever sent. See the PII guard in events.ts.
 *
 * To go live, see src/lib/analytics/README.md.
 */

import type { AnalyticsEvent } from './events';

const SENSITIVE_ANALYTICS_PROPERTIES = [
  '$current_url', '$referrer', '$referring_domain', '$host', '$pathname',
  '$initial_current_url', '$initial_referrer', '$initial_referring_domain', '$initial_host', '$initial_pathname',
  '$session_entry_url', '$session_entry_referrer', '$session_entry_referring_domain', '$session_entry_host', '$session_entry_pathname',
  '$session_id', '$initial_person_info', '$initial_referrer_info',
] as const;

type AnalyticsProperties = Record<string, unknown>;

interface CaptureEventLike {
  event: string;
  properties?: AnalyticsProperties;
  $set?: AnalyticsProperties;
  $set_once?: AnalyticsProperties;
}

/**
 * Minimal structural type for the slice of the posthog-js client we use.
 * Keeps this provider decoupled from PostHog's wider client surface and avoids
 * `any` in the event path.
 */
interface PosthogClientLike {
  init: (
    key: string,
    options: {
      api_host: string;
      capture_pageview: boolean;
      autocapture: boolean;
      person_profiles: 'identified_only';
      property_denylist: readonly string[];
      before_send: (event: CaptureEventLike | null) => CaptureEventLike | null;
    },
  ) => void;
  capture: (event: string, properties?: Record<string, unknown>) => void;
  identify: (distinctId: string, properties?: Record<string, unknown>) => void;
  reset: () => void;
  opt_in_capturing?: () => void;
  opt_out_capturing?: () => void;
}

/** Shape of the posthog-js default export, narrowed to what we consume. */
interface PosthogModuleLike {
  default: PosthogClientLike;
}

let client: PosthogClientLike | null = null;
let initialized = false;
let privacyContext = {
  hasConsent: false,
  distinctId: undefined as string | undefined,
};

function readKey(): string | undefined {
  if (import.meta.env.VITE_ENABLE_ANALYTICS !== 'true') {
    return undefined;
  }

  const key = import.meta.env.VITE_POSTHOG_KEY;
  return key && key.length > 0 ? key : undefined;
}

function readHost(): string {
  return import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com';
}

function hasBrowserPrivacyOptOut(): boolean {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') {
    return true;
  }

  const nav = navigator as Navigator & { globalPrivacyControl?: boolean; msDoNotTrack?: string };
  const win = window as Window & { doNotTrack?: string };
  const doNotTrack = nav.doNotTrack || nav.msDoNotTrack || win.doNotTrack;
  return nav.globalPrivacyControl === true || doNotTrack === '1' || doNotTrack === 'yes';
}

function isChatGPTConsentUrl(value: string, depth = 0): boolean {
  if (depth > 2 || typeof window === 'undefined') return false;

  try {
    const url = new URL(value, window.location.origin);
    if (url.pathname === '/chatgpt/consent') return true;

    const returnTo = url.searchParams.get('returnTo');
    return Boolean(returnTo && isChatGPTConsentUrl(returnTo, depth + 1));
  } catch {
    return false;
  }
}

/** OAuth consent request IDs are URL-borne secrets and must never reach analytics. */
export function isSensitiveAnalyticsLocation(): boolean {
  return typeof window !== 'undefined' && isChatGPTConsentUrl(window.location.href);
}

function scrubSensitiveProperties(properties: AnalyticsProperties | undefined): AnalyticsProperties | undefined {
  if (!properties) return properties;

  const cleanProperties = { ...properties };
  for (const property of SENSITIVE_ANALYTICS_PROPERTIES) {
    delete cleanProperties[property];
  }
  cleanProperties.$set = scrubSensitiveProperties(
    cleanProperties.$set as AnalyticsProperties | undefined,
  );
  cleanProperties.$set_once = scrubSensitiveProperties(
    cleanProperties.$set_once as AnalyticsProperties | undefined,
  );
  return cleanProperties;
}

function eventOriginatesOnConsentRoute(properties: AnalyticsProperties | undefined): boolean {
  if (!properties) return false;
  const currentUrl = properties.$current_url ?? properties.current_url;
  return typeof currentUrl === 'string' && isChatGPTConsentUrl(currentUrl);
}

function beforeSend(event: CaptureEventLike | null): CaptureEventLike | null {
  if (!event || isSensitiveAnalyticsLocation() || eventOriginatesOnConsentRoute(event.properties)) return null;

  return {
    ...event,
    properties: scrubSensitiveProperties(event.properties),
    $set: scrubSensitiveProperties(event.$set),
    $set_once: scrubSensitiveProperties(event.$set_once),
  };
}

function canCapture(): boolean {
  return privacyContext.hasConsent && !hasBrowserPrivacyOptOut() && !isSensitiveAnalyticsLocation();
}

function deactivateClient(): void {
  if (client) {
    client.reset();
    client.opt_out_capturing?.();
    client = null;
  }
  initialized = false;
}

export function setAnalyticsPrivacyContext(next: {
  hasConsent: boolean;
  distinctId?: string;
}): void {
  const wasAllowed = canCapture();
  privacyContext = {
    hasConsent: next.hasConsent === true,
    distinctId: next.distinctId,
  };
  const isAllowed = canCapture();

  if (!isAllowed) {
    deactivateClient();
    return;
  }

  if (!wasAllowed || !client) {
    initialized = false;
  }
}

/**
 * Initialize analytics. Safe to call multiple times. Resolves to `true` only
 * when a real client was created (key present, consent allowed, package loaded).
 */
export async function init(): Promise<boolean> {
  if (initialized) {
    // The route can change without a privacy-context update. Ensure a
    // previously initialized client cannot remain active on a consent URL.
    if (!canCapture()) {
      deactivateClient();
      return false;
    }
    return client !== null;
  }

  const key = readKey();
  if (!key) {
    // No key → permanently inert for this session. This is the default path.
    initialized = true;
    return false;
  }

  if (!canCapture()) {
    return false;
  }

  try {
    // Guarded dynamic import keeps analytics off the critical path while still
    // letting Vite bundle posthog-js as an installed dependency.
    const mod = (await import('posthog-js')) as unknown as PosthogModuleLike;
    const ph = mod.default;
    // Route state can change while posthog-js is loading. Recheck before its
    // initialization captures initial URL/referrer properties.
    if (!canCapture()) return false;
    ph.init(key, {
      api_host: readHost(),
      capture_pageview: false, // we send page_viewed explicitly via track()
      autocapture: false, // no DOM scraping — explicit events only, no PII
      person_profiles: 'identified_only',
      property_denylist: SENSITIVE_ANALYTICS_PROPERTIES,
      before_send: beforeSend,
    });
    ph.opt_in_capturing?.();
    client = ph;
    initialized = true;
    if (privacyContext.distinctId) {
      identify(privacyContext.distinctId);
    }
    return true;
  } catch {
    // Package missing or init failed — stay a no-op. Never throw to callers.
    client = null;
    initialized = true;
    return false;
  }
}

/**
 * Associate subsequent events with an opaque user id.
 * Pass ONLY a non-PII identifier (e.g. Firebase UID). No-op without a client.
 */
export function identify(distinctId: string): void {
  if (!client || !canCapture()) return;
  client.identify(distinctId);
}

/** Record a typed event. No-op without a client. */
export function track(event: AnalyticsEvent): void {
  if (!client || !canCapture()) return;
  // `properties` is already constrained to non-PII primitive shapes by events.ts.
  client.capture(event.name, event.properties);
}

/** Clear the identified user (call on logout). No-op without a client. */
export function reset(): void {
  if (!client) return;
  client.reset();
}

/** True when a real analytics client is active (key present + package loaded). */
export function isActive(): boolean {
  return client !== null;
}
